import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { Link } from "react-router-dom";

const aboutStructuredData = [
  {
    "@type": "WebPage",
    name: "開発者の想い",
    description:
      "ハヤブサワークスのブランドストーリーと開発者の想い。3Dプリント技術で「飛ぶ」を極める釣り具を作る理由、そして島根から世界へ届ける情熱。",
    url: "https://raigeki-hayabusa.com/about",
    inLanguage: "ja",
    isPartOf: {
      "@type": "WebSite",
      name: "ハヤブサワークス",
      url: "https://raigeki-hayabusa.com",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ホーム",
          item: "https://raigeki-hayabusa.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "開発者の想い",
        },
      ],
    },
  },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100">
      <SEOHead
        title="開発者の想い｜ハヤブサワークス"
        description="ハヤブサワークスのブランドストーリーと開発者の想い。3Dプリント技術で「飛ぶ」を極める釣り具を作る理由、そして島根から世界へ届ける情熱。"
        keywords="ハヤブサワークス,ブランドストーリー,開発者の想い,3Dプリント 釣り具,雷撃カゴ 開発"
        canonicalPath="/about"
        ogImage="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/cfb5afee631d88a7b12672494ae842fb.jpeg"
        structuredData={aboutStructuredData}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/809f924ef6ff25d77e1cb8a3a7a74e2b.png"
            alt="開発者の想い"
            title="開発者の想い"
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 40%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span
              className="text-background-50/80 text-xs tracking-[0.4em] mb-6"
              style={{ fontFamily: "var(--font-label)" }}
            >
              ABOUT US
            </span>
            <h1
              className="text-background-50 text-3xl md:text-5xl leading-[1.05] max-w-4xl whitespace-nowrap"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              開発者の想い
            </h1>
            <p className="text-background-50/80 text-sm md:text-base max-w-xl mt-6 leading-relaxed">
              「飛ぶ」を極めし、すべてのアングラーへ捧ぐ
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-12">
            <div className="lg:w-[42%]">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-[2px] bg-primary-500"></span>
                <span
                  className="text-primary-500 text-xs tracking-[0.4em]"
                  style={{ fontFamily: "var(--font-label)" }}
                >
                  BRAND STORY
                </span>
              </div>
              <h2
                className="text-foreground-950 text-3xl md:text-5xl leading-[1.1] mb-8"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                はじまりは、
                <br />
                父との絆から
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  ハヤブサワークスは、島根県松江市で生まれた小さな釣具ブランドです。創業者である私自身、長年フカセ釣りやカゴ釣りやルアー釣りに没頭するアングラーでした。
                </p>
                <p>
                  なんの取り柄のない私に釣りを教えてくれたの父でした。父は山陰発祥のタルカゴ釣りが好きでした。
                </p>
                <p>
                  父は典型的な週末アングラーで力もあまりありませんでした。さらに晩年は手の力が弱くなり、遠くにタルカゴを飛ばすことができなくなりました。
                </p>
                <p>
                  誰でも遠くに飛ばせるカゴが欲しい。でも今あるカゴには限界がある。そう思いながら何度もタルカゴを投げ続けた日々の中で、「もし自分で作れるなら」という想いが芽生えました。
                </p>
                <p>
                  3Dプリンタを導入し、CADで設計を始めたのは父がいなくなった最初の冬のこと。力のない人でも遠くに飛ばせるものが作りたい。誰もが遠くに飛ばす楽しみを味わって欲しい。
                </p>
                <p>
                  そのためにはカゴの空気抵抗を最大まで小さくしなくてはならない。最初の試作品は、部屋の隅で何度も何度も何度も失敗を繰り返した末に生まれた、粗削りな一個のカゴでした。
                </p>
                <p>
                  しかし、そのカゴを海に投げた瞬間、今までにない軌道で飛んでいく様子を目の当たりにした時、私は確信しました。「これは釣りを変える道具だ」と。
                </p>
                <p>
                  雷撃カゴの物語はここから始まります。
                </p>
              </div>
            </div>
            <div className="lg:w-[58%]">
              <div className="grid grid-cols-2 gap-4">
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/1adb6d5a33fc9a1eebccd3e8d866f998.jpeg"
                    alt="最初の試作品"
                    title="最初の試作品"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 50%" }}
                  />
                </div>
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/fdd1fb3da38c498d2ad0e9f4ad36edec.jpeg"
                    alt="雷撃カゴを投げる瞬間"
                    title="雷撃カゴを投げる瞬間"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 50%" }}
                  />
                </div>
              </div>
              <div className="mt-4 max-w-lg mx-auto">
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://static.readdy.ai/image/6598d90c230f37b72c6551167ff1d4d5/40f9b240a1d504c31abf8f8071a030d3.jpeg"
                    alt="実釣で大きな鯛を釣り上げた瞬間"
                    title="実釣で大きな鯛を釣り上げた瞬間"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 50%" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="w-full bg-background-100 border-y border-background-200/60">
          <div className="px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
            <div className="text-center mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="w-8 h-[2px] bg-accent-500"></span>
                <span
                  className="text-accent-500 text-xs tracking-[0.4em]"
                  style={{ fontFamily: "var(--font-label)" }}
                >
                  PHILOSOPHY
                </span>
                <span className="w-8 h-[2px] bg-accent-500"></span>
              </div>
              <h2
                className="text-foreground-950 text-3xl md:text-5xl leading-[1.1]"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                なぜ、3Dプリントなのか
              </h2>
            </div>

            <div className="max-w-4xl mx-auto space-y-8 text-foreground-600 text-sm md:text-base leading-relaxed">
              <p>
                従来のカゴ製造は金型に頼るため、内部構造に限界がありました。しかし3Dプリント技術なら、金型では不可能な中空構造や複雑なリブ、最適化された重心配置を、一層ずつ精密に積み上げることができます。
              </p>
              <p>
                私たちが目指すのは「量産性」ではありません。一つ一つのカゴが、設計データの通りに忠実に造形される「再現性」と、空気抵抗を極限まで削ぎ落とした「飛行性能」です。それは、デジタル設計と物理世界が交差する瞬間にしか生まれない価値です。
              </p>
              <p>
                島根の小さな工房から、世界に通用する性能を持つ道具を生み出す。それが私たちの誇りであり、これからも変わることのない信念です。
              </p>
            </div>

            {/* Quote */}
            <div className="mt-16 text-center">
              <div className="inline-block border-l-4 border-primary-500 pl-6">
                <p
                  className="text-foreground-950 text-xl md:text-3xl leading-relaxed italic"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  飛ばす楽しみ。釣る悦び。ずっと忘れない。
                  <br />
                  その衝撃、雷撃の如し。
                </p>
              </div>
              <p className="text-foreground-600 text-sm mt-4 tracking-widest">
                ―― 雷撃カゴ――
              </p>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="w-full px-6 md:px-10 max-w-[1600px] mx-auto py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {visions.map((v) => (
              <div
                key={v.id}
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60 hover:border-primary-500/30 transition-colors duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50 mb-5">
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h4
                  className="text-foreground-950 text-lg mb-3"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {v.title}
                </h4>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {v.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20aerial%20view%20of%20calm%20blue%20ocean%20coastline%20at%20golden%20hour%20with%20warm%20sunset%20light%2C%20gentle%20waves%20meeting%20sandy%20shore%2C%20distant%20silhouette%20of%20small%20island%2C%20serene%20and%20hopeful%20mood%2C%20cinematic%20landscape%20photography%20with%20soft%20natural%20gradients&width=1600&height=600&seq=about-cta-2026&orientation=landscape"
            alt="未来への展望"
            title="未来への展望"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "50% 60%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/35 to-white/70"></div>
          <div className="relative px-6 md:px-10 max-w-[1600px] mx-auto py-24 md:py-32 flex flex-col items-center text-center">
            <h2
              className="text-foreground-950 text-3xl md:text-5xl leading-[1.15] max-w-2xl mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              島根から、世界の海へ
            </h2>
            <p className="text-foreground-700 text-sm md:text-base max-w-lg mb-10 leading-relaxed">
              雷撃カゴはまだ始まったばかりです。これからもアングラーの声を聴き、一投先を見つめ続けます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/#product"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-xs tracking-[0.3em] whitespace-nowrap hover:bg-primary-600 transition cursor-pointer"
                style={{ fontFamily: "var(--font-label)" }}
              >
                製品を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/#contact"
                className="px-8 py-3.5 rounded-full border border-foreground-300 text-foreground-700 text-xs tracking-[0.3em] whitespace-nowrap hover:bg-foreground-100/20 transition cursor-pointer"
                style={{ fontFamily: "var(--font-label)" }}
              >
                お問い合わせ
                <i className="ri-mail-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

const visions = [
  {
    id: "vision-1",
    icon: "ri-cpu-line",
    title: "技術の民主化",
    description:
      "3Dプリント技術を駆使し、大企業に頼らずとも世界レベルの性能を持つ釣具を生み出す。小さな工房から大きなイノベーションを起こすことが、私たちの使命です。",
  },
  {
    id: "vision-2",
    icon: "ri-heart-3-line",
    title: "アングラー第一主義",
    description:
      "すべての設計判断は、現場で投げる人の視点から始まります。「実釣で使えるか」を最優先に据え、理論と現場のフィードバックを常に往復させています。",
  },
  {
    id: "vision-3",
    icon: "ri-earth-line",
    title: "島根から世界へ",
    description:
      "日本海に面した島根の地から、世界の釣り人に向けて「飛ぶ」体験を届ける。そんな野望を抱き、今日も工房のプリンタは静かに動き続けています。",
  },
];